import type { Middleware } from '@reduxjs/toolkit';
import { isFulfilled, isRejectedWithValue } from '@reduxjs/toolkit';
import { track } from '../lib/analytics';

/**
 * Middleware que escucha mutations y queries de RTK Query y las trackea como
 * eventos de analytics. Solo emite cuando el usuario está logueado en el CRM.
 *
 * - Mutations exitosas → type 'action' con la action derivada del endpoint name.
 * - Cualquier query/mutation con error → type 'error' con detalles.
 *
 * No bloquea ni demora — todo va vía track() que es best-effort.
 */

/**
 * Endpoints de "preview" best-effort: inline cards que Gustavo arma desde links del
 * texto del modelo (que puede alucinar un id inexistente). Un 404 acá es benigno —
 * el componente muestra un fallback — y NO debe registrarse como error accionable.
 */
const SOFT_PREVIEW_ENDPOINTS = new Set(['getCustomerCard', 'getPropertyCard']);

// Polls de fondo (GET de solo-lectura que corren solos en cada página, cada 30-60s).
// Cuando el backend se reinicia (deploys) TODOS fallan a la vez con 5xx/401 → cascadas
// que inflan el panel de errores agrupadas por la ruta donde estaba el usuario, aunque
// no sean bugs. Los errores TRANSITORIOS (5xx / auth) de estos NO se registran; RTK
// reintenta solo y el poll se recupera. Un 4xx real distinto (400/422) sí se registra.
const BACKGROUND_POLL_ENDPOINTS = new Set([
  'getNotifications', 'getMyProfile', 'getWalletMe', 'getMyCredits', 'getActiveSurvey',
  'listConversations', 'getRecentBlogPosts', 'getResolvedFlags', 'listOpenIssues',
  'getMeliMetrics', 'getOnlineAgents', 'getCustomerCard', 'getPropertyCard',
]);

export const analyticsMiddleware: Middleware = (store) => (next) => (action: any) => {
  const result = next(action);

  // Solo trackear si hay usuario CRM logueado
  const state: any = store.getState();
  const isAuth = state?.auth?.isAuthenticated;
  if (!isAuth) return result;

  // RTK Query actions tienen un meta.arg.endpointName y type tipo 'api/executeMutation/fulfilled'
  const meta = action?.meta;
  if (!meta?.arg) return result;

  const endpointName: string | undefined = meta.arg.endpointName;
  const type: string | undefined         = meta.arg.type;
  if (!endpointName || !type) return result;

  // Detectar errores en cualquier endpoint
  if (isRejectedWithValue(action)) {
    const status = action.payload?.status;
    // No registramos errores de NIVEL DE RED (FETCH_ERROR/TIMEOUT_ERROR/PARSING_ERROR): la
    // request no obtuvo una respuesta JSON usable — o no llegó al servidor (conexión del
    // usuario, backend inalcanzable unos segundos en un redeploy) o el gateway devolvió una
    // página HTML de error (5xx/504) que RTK no pudo parsear (PARSING_ERROR). Son transitorios,
    // no accionables, y RTK reintenta solo — antes inflaban el panel de soporte con ráfagas.
    // Sí registramos los errores HTTP reales (4xx/5xx) y los de app.
    if (status === 'FETCH_ERROR' || status === 'TIMEOUT_ERROR' || status === 'PARSING_ERROR') return result;
    // Errores de GATEWAY (502 Bad Gateway / 503 Service Unavailable / 504 Timeout):
    // el backend estaba caído/reiniciándose (deploys) — nunca es un bug de la app, es
    // infra transitoria. RTK reintenta y se recupera. Eran el grueso del ruido del panel.
    if (status === 502 || status === 503 || status === 504) return result;
    // 404 benigno de "preview" best-effort: las inline cards de Gustavo (cliente/propiedad) se
    // arman desde links del texto del modelo, que a veces alucina un id inexistente. La card ya
    // muestra un fallback, así que el 404 no es accionable y no debe ensuciar el panel.
    if (status === 404 && SOFT_PREVIEW_ENDPOINTS.has(endpointName)) return result;
    // Poll de fondo caído por un reinicio del backend (5xx) o token expirado (401/403):
    // transitorio, no accionable, se recupera solo. No lo mostramos como error.
    if (BACKGROUND_POLL_ENDPOINTS.has(endpointName)
      && (status === 401 || status === 403 || (typeof status === 'number' && status >= 500))) return result;
    track({
      type:     'error',
      category: 'agent',
      action:   `${endpointName}_failed`,
      label:    typeof status === 'number' ? `HTTP ${status}` : String(status ?? 'unknown'),
      meta:     { endpointName, status, errorData: action.payload?.data },
    });
    return result;
  }

  // Solo trackear MUTATIONS exitosas — las queries serían demasiado ruidosas
  if (type !== 'mutation' || !isFulfilled(action)) return result;

  // Mutations que NO queremos trackear (lecturas disfrazadas, polling, etc.)
  const SKIP = new Set(['refreshToken', 'getMe', 'logout']);
  if (SKIP.has(endpointName)) return result;

  // Resource type derivado del prefijo del endpoint (createCustomer → customer)
  const m = endpointName.match(/^(create|update|delete|reassign|transfer|toggle|change|complete|cancel|approve|reject|skip|reset)([A-Z]\w+)/);
  let resourceType: string | undefined;
  if (m && m[2]) {
    // "Customer" → "customer", "PropertyStatus" → "property"
    resourceType = m[2].replace(/([A-Z][a-z]+)([A-Z]\w*)?/, '$1').toLowerCase();
  }

  track({
    type:         'action',
    category:     'agent',
    action:       endpointName,
    resourceType,
    meta:         { trackingMutationId: meta.requestId },
  });

  return result;
};
